import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../../context/authContext";
import createAxiosInstance from "../../../utils/axiosConfig";
import {
	defaultUserSearchColumns,
	userLoggedInColumns,
	userLoginActivityColumns,
	conversationHistoryColumns,
} from "../../../utils/constants";
import AdminTables from "../../common/adminTables";
import Messages from "../../common/messages";
import { ConfirmationModal } from "../../common/modal";
import AdminLayout from "../../common/adminLayout";

const UserReporting = () => {
	const navigate = useNavigate();
	const { userData, logout } = useAuth();
	const [formData, setFormData] = useState<{
		results: [];
		total_count: number;
	}>({ results: [], total_count: 0 });
	const [useCase, setUseCase] = useState("chat_errors"); // New state for use case
	const [currentPage, setCurrentPage] = useState(1); // New state for current page
	const [itemsPerPage, setItemsPerPage] = useState(10); // New state for items per page
	const [isLoading, setIsLoading] = useState(true);
	const [showModal, setShowModal] = useState(false);
	const [selectedRowData, setSelectedRowData] = useState<any>(null);

	const getChatErrors = async () => {
		try {
			const response = await createAxiosInstance().get("/chat_error-logs", {
				params: {
					offset: (currentPage - 1) * itemsPerPage,
					limit: itemsPerPage,
				},
			});

			return response;
		} catch (error: any) {
			if (error.status == 401 || error.status == 403) {
				logout();
				navigate("/");
			}
			console.log(error, "error");
			return { data: { results: [], total_count: 0 } };
		}
	};

	const getSearchActivity = async () => {
		try {
			const response = await createAxiosInstance().get("/chat/user_search_activity", {
				params: {
					offset: (currentPage - 1) * itemsPerPage,
					limit: itemsPerPage,
				},
			});

			return response;
		} catch (error: any) {
			if (error.status == 401 || error.status == 403) {
				logout();
				navigate("/");
			}
			console.log(error, "error");
			return { data: { results: [], total_count: 0 } };
		}
	};

	const getLoginActivity = async () => {
		try {
			const response = await createAxiosInstance().get("/login-activity", {
				params: {
					offset: (currentPage - 1) * itemsPerPage,
					limit: itemsPerPage,
				}
			});
			return response;
		} catch (error: any) {
			if (error.status == 401 || error.status == 403) {
				logout();
				navigate("/");
			}
			console.log(error, "error");
			return { data: { results: [], total_count: 0 } };
		}
	};

	const getActiveUsers = async () => {
		try {
			const response = await createAxiosInstance().get("/logged-in-users", {
				params: {
					offset: (currentPage - 1) * itemsPerPage,
					limit: itemsPerPage,
				}
			});
			return response;
		} catch (error: any) {
			console.log(error, "error");
			if (error.status == 401 || error.status == 403) {
				logout();
				navigate("/");
			}
			return { data: { results: [], total_count: 0 } };
		}
	};

	const getFormData = async () => {
		try {
			setIsLoading(true);
			setFormData({ results: [], total_count: 0 });
			if (useCase === "chat_errors") {
				const response = await getChatErrors();
				setFormData(response.data);
			} else if (useCase === "login_activity") {
				const response = await getLoginActivity();
				setFormData(response.data);
			} else if (useCase === "activeUsers") {
				const response = await getActiveUsers();
				setFormData(response.data);
			}else if (useCase === "search_activity"){
				const response = await getSearchActivity();
				// Filter out entries with empty conversation arrays
				if (response.data && response.data.results) {
					const filteredResults = response.data.results.filter(
						(item: any) => item.conversation && item.conversation.length > 0
					);
					setFormData({
						results: filteredResults,
						total_count: response.data.total_count
					});
				} else {
					setFormData(response.data);
				}
			}

		} catch (error: any) {
			if (error.status == 401 || error.status == 403) {
				logout();
				navigate("/");
			}
			console.log(error, "error");
		} finally {
			setIsLoading(false);
		}
	};

	const handleRowSelection = (rowData:any)=>{
		setSelectedRowData(rowData);
		setShowModal(true);
	}

	const onClose = ()=>{
		setSelectedRowData(null);
		setShowModal(false);
	}

	useEffect(() => {
		getFormData();
	}, [useCase, currentPage, itemsPerPage]);

	useEffect(() => {
		if (userData && !userData?.is_admin) {
			navigate("/");
		}
	}, [userData]);


	const modalBody = (rowData:any)=>{
		const formattedMessages = rowData?.conversation?.flatMap((msg: any) => [
			// Create question message (right side)
			msg.question ? {
			type: 'right',
			text: msg.question || '',
			time: msg.timestamp || new Date().toLocaleTimeString(),
			icon: false,
			typewriter: false,
			} : null,
			// Create response message (left side)
			msg.response ? {
			type: 'left',
			text: msg.response || '',
			time: msg.timestamp || new Date().toLocaleTimeString(),
			icon: false,
			typewriter: false,
			} : null
		].filter(Boolean)) || []; // Filter out null items

		return (<div className="" style={{
			marginTop: '18%',
			maxHeight: '80%',
			textAlign: 'left'
		}}>
			{rowData && <Messages
				useCase={rowData?.conversation[0]?.use_case}
				messages={formattedMessages}
				chat_id={rowData?.id}
				handleOptionSelect={()=>{}}
				handleUseCaseSelect={()=>{}}
				handleSubCase={()=>{}}
				showMessage={false}
				selectedSubCase={
					rowData?.conversation[0]?.label.split('.').length >= 1 ? rowData?.conversation[0]?.label.split('.')[1] : ''
				}
				setDisabled={()=>{}}
			/>}
		</div>);
	}

	// Create action panel component
	const UseCaseSelector = () => (
		<>
			<label htmlFor="floatingSelect">Select a use case:</label>
			<select
				value={useCase}
				onChange={(e) => {
					setUseCase(e.target.value);
					setCurrentPage(1);
					setItemsPerPage(10);
				}}
				className="form-select mb-4"
				id="floatingSelect"
				aria-label="Floating label select example"
			>
				<option value="search_activity">
					Search Activity (users)
				</option>
				<option value="chat_errors">Error Logs (chat)</option>
				<option value="login_activity">Login Activity</option>
				<option value="activeUsers">Active Users</option>
			</select>
		</>
	);
	
	const getColumnsForUseCase = (useCase: string) => {
		switch (useCase) {
			case "chat_errors":
				return defaultUserSearchColumns;
			case "login_activity":
				return userLoginActivityColumns;
			case "search_activity":
				return conversationHistoryColumns;
			default:
				return userLoggedInColumns;
		}
	}
	
	return (
		<AdminLayout
			title="User Reporting"
			actionPanel={<UseCaseSelector />}
			formData={formData}
			currentPage={currentPage}
			setCurrentPage={setCurrentPage}
			itemsPerPage={itemsPerPage}
			setItemsPerPage={setItemsPerPage}
		>
			<AdminTables
				data={formData.results}
				columns={getColumnsForUseCase(useCase)}
				rowSelectionEnabled={useCase == 'search_activity'}
				onRowSelect={useCase == 'search_activity' ? handleRowSelection : ()=>{}}
				isLoading={isLoading}
			/>
			<ConfirmationModal
				show={showModal}
				onClose={onClose}
				closeText={'Close'}
				title={'Conversation'}
				body={modalBody(selectedRowData)} 
				maxHeight={"100vh"}
				maxWidth={'80vw'}
			/>
		</AdminLayout>
	);
};

export default UserReporting;
